/**
 * GT Brokers design tokens.
 *
 * The single source for radius, elevation, control sizing and motion across the
 * portal. Colour is NOT defined here — it lives in `appcolors.ts`, because an
 * installation can override every colour from Setting → Theme Color and that
 * configurability must survive this redesign.
 *
 * Anything importing from here gets the same geometry as everything else, which
 * is most of what makes a large application read as one product rather than as
 * forty screens built by different people.
 */

/** Corner radii. Cards sit at 16; controls at 10; pills are fully round. */
export const radius = {
  xs: 6,
  sm: 8,
  md: 10,
  lg: 14,
  xl: 16,
  xxl: 20,
  pill: 999,
} as const;

/**
 * Control heights. Every interactive control in the app lands on one of these,
 * so a filter bar full of mixed inputs and buttons aligns without per-page
 * nudging.
 */
export const control = {
  sm: 34,
  md: 40,
  lg: 46,
  /** Compact square icon action used in table rows. */
  action: 34,
} as const;

/** Layout constants shared by the shell. */
export const layout = {
  topbarHeight: 76,
  sidebarWidth: 268,
  pageMaxWidth: 1680,
  gutter: 24,
  gutterMobile: 14,
} as const;

/**
 * Restrained elevation. Premium software casts short, soft, mostly-vertical
 * shadows; long blurred haloes are what make a UI look like a template.
 */
export const shadow = {
  none: "none",
  /** Resting card. */
  card: "0 1px 2px rgba(16, 32, 52, 0.04), 0 1px 3px rgba(16, 32, 52, 0.03)",
  /** Card on hover, and the filter bar. */
  raised: "0 2px 4px rgba(16, 32, 52, 0.05), 0 6px 16px rgba(16, 32, 52, 0.06)",
  /** Menus, popovers, dropdowns. */
  overlay: "0 6px 16px rgba(16, 32, 52, 0.08), 0 18px 40px rgba(16, 32, 52, 0.10)",
  /** Dialogs. */
  dialog: "0 12px 28px rgba(16, 32, 52, 0.12), 0 32px 64px rgba(16, 32, 52, 0.16)",
  /** Dark mode runs on depth rather than shadow, so these are near-flat. */
  cardDark: "0 1px 2px rgba(0, 0, 0, 0.30)",
  raisedDark: "0 4px 14px rgba(0, 0, 0, 0.36)",
  overlayDark: "0 10px 30px rgba(0, 0, 0, 0.46)",
  dialogDark: "0 24px 60px rgba(0, 0, 0, 0.56)",
} as const;

/**
 * Motion. Everything sits in the 160–260ms band the brief asks for; anything
 * longer reads as sluggish in an application people use all day.
 */
export const motion = {
  fast: 160,
  base: 200,
  slow: 260,
  easing: "cubic-bezier(0.4, 0, 0.2, 1)",
} as const;

/** `transition` shorthand for a list of properties at one of the speeds above. */
export const transition = (
  properties: string[],
  duration: number = motion.base
): string => properties.map((p) => `${p} ${duration}ms ${motion.easing}`).join(", ");

/**
 * Type scale. Sizes are px numbers so they can be handed straight to `sx`
 * without unit juggling.
 */
export const type = {
  pageTitle: 27,
  pageTitleSm: 22,
  pageSubtitle: 13.5,
  sectionTitle: 18,
  cardTitle: 15.5,
  tableHeader: 11.5,
  body: 13.5,
  bodyStrong: 13.5,
  meta: 11.5,
  kpi: 34,
} as const;

/** Reduced-motion guard to spread into any `sx` that animates. */
export const reducedMotion = {
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
    animation: "none",
  },
} as const;
